/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * SQLite Migration Manager
 * Handles database schema versioning and migrations for native platforms (iOS/Android)
 * 
 * HOW IT WORKS:
 * - SQLite stores version in PRAGMA user_version (integer)
 * - On app startup, we compare stored version vs expected version
 * - If stored < expected, we run migrations sequentially
 * - Each migration runs in a transaction (auto-rollback on error)
 * 
 * PROCESS:
 * 1. Read PRAGMA user_version → e.g., 2
 * 2. Compare with CURRENT_VERSION → e.g., 4
 * 3. Run migrations: v2→v3, then v3→v4
 * 4. Update PRAGMA user_version to 4
 * 
 * IMPORTANT:
 * - Never skip version numbers (always sequential: 1→2→3→4)
 * - Migrations are permanent (no rollback after commit)
 * - Test thoroughly before production deployment
 * 
 * @see docs/MIGRATIONS.md for detailed guide
 */
export class SQLiteMigrationManager {
  constructor(db) {
    this.db = db
    this.CURRENT_VERSION = 3 // Updated Jan 2026: eartag is now optional
    
    // Migration definitions
    // Index corresponds to version number
    this.migrations = [
      null, // index 0 (no migration)
      null, // index 1 (baseline - no migration needed)
      
      // =================================================================
      // FUTURE MIGRATIONS - Add new versions below
      // =================================================================
      
      // VERSION 2 - Notes Array → String (January 2026)
      // This is a data-only migration - schema already uses TEXT for notes
      // Converts JSON array strings to plain newline-separated strings
      {
        version: 2,
        description: 'Convert goat notes from JSON array to plain string',
        up: async (db) => {
          console.log('[SQLite v1→v2] Converting notes from array to string...')
          
          // Query all goats
          const result = await db.query('SELECT id, notes FROM goats WHERE notes IS NOT NULL AND notes != ""', [])
          const goats = result.values || []
          
          let migratedCount = 0
          
          for (const goat of goats) {
            const notes = goat.notes
            
            // Check if notes is a JSON array string
            if (notes && (notes.startsWith('[') || notes.startsWith('{"'))) {
              try {
                const notesArray = JSON.parse(notes)
                
                if (Array.isArray(notesArray)) {
                  // Convert array of note objects to newline-separated string
                  const newNotes = notesArray
                    .map(note => note?.text || '')
                    .filter(text => text.trim())
                    .join('\n') || null
                  
                  await db.run(
                    'UPDATE goats SET notes = ?, updated_at = CURRENT_TIMESTAMP WHERE id = ?',
                    [newNotes, goat.id]
                  )
                  migratedCount++
                }
              } catch (error) {
                // Not valid JSON, leave as-is (might already be plain string)
                console.warn(`[SQLite v1→v2] Skipping goat ${goat.id}: notes not valid JSON`)
              }
            }
          }
          
          console.log(`[SQLite v1→v2] Migration complete: ${migratedCount} goats updated`)
        }
      },
      
      // VERSION 3 - Eartag is now optional (January 2026)
      // Remove NOT NULL constraint from eartag field
      // SQLite doesn't support dropping constraints, so we need to recreate the table
      {
        version: 3,
        description: 'Make eartag optional (remove NOT NULL constraint)',
        up: async (db) => {
          console.log('[SQLite v2→v3] Making eartag optional...')
          
          // Step 1: Rename old table
          await db.execute('ALTER TABLE goats RENAME TO goats_old')
          
          // Step 2: Create new table without NOT NULL on eartag
          await db.execute(`
            CREATE TABLE goats (
              id INTEGER PRIMARY KEY AUTOINCREMENT,
              name TEXT,
              date_of_birth TEXT NOT NULL,
              place_of_birth TEXT,
              breed TEXT NOT NULL,
              eartag TEXT UNIQUE,
              gender TEXT NOT NULL CHECK(gender IN ('Male', 'Female')),
              notes TEXT,
              group_id INTEGER,
              created_at TEXT DEFAULT CURRENT_TIMESTAMP,
              updated_at TEXT DEFAULT CURRENT_TIMESTAMP,
              FOREIGN KEY (group_id) REFERENCES groups(id) ON DELETE SET NULL
            )
          `)
          
          // Step 3: Copy data from old table
          await db.execute(`
            INSERT INTO goats (id, name, date_of_birth, place_of_birth, breed, eartag, gender, notes, group_id, created_at, updated_at)
            SELECT id, name, date_of_birth, place_of_birth, breed, eartag, gender, notes, group_id, created_at, updated_at
            FROM goats_old
          `)
          
          // Step 4: Recreate indexes
          await db.execute('CREATE INDEX IF NOT EXISTS idx_goats_name ON goats(name)')
          await db.execute('CREATE INDEX IF NOT EXISTS idx_goats_group_id ON goats(group_id)')
          await db.execute('CREATE INDEX IF NOT EXISTS idx_goats_breed ON goats(breed)')
          await db.execute('CREATE INDEX IF NOT EXISTS idx_goats_eartag ON goats(eartag)')
          
          // Step 5: Drop old table
          await db.execute('DROP TABLE goats_old')
          
          console.log('[SQLite v2→v3] Migration complete: eartag is now optional')
        }
      },
    ]
  }

  /**
   * Initialize migration system and run pending migrations
   * @param {number} currentVersion - Current database version
   * @returns {Promise<void>}
   */
  async initialize(currentVersion) {
    if (currentVersion < this.CURRENT_VERSION) {
      console.log(`Migration needed: v${currentVersion} → v${this.CURRENT_VERSION}`)
      await this.runMigrations(currentVersion)
    } else if (currentVersion === this.CURRENT_VERSION) {
      console.log(`SQLite already at v${currentVersion}`)
    } else {
      throw new Error(
        `Database version (${currentVersion}) is newer than app version (${this.CURRENT_VERSION}). ` +
        'Please update the app.'
      )
    }
  }

  /**
   * Get current database schema version
   * @returns {Promise<number>} Current version number
   */
  async getCurrentVersion() {
    try {
      const result = await this.db.query('PRAGMA user_version', [])
      return result.values[0]?.user_version || 0
    } catch (error) {
      console.error('Failed to get database version:', error)
      return 0
    }
  }

  /**
   * Set database schema version
   * @param {number} version - Version number to set
   * @returns {Promise<void>}
   */
  async setVersion(version) {
    await this.db.execute(`PRAGMA user_version = ${version}`)
    console.log(`Database version updated to ${version}`)
  }

  /**
   * Run all pending migrations
   * @private
   * @param {number} fromVersion - Starting version
   * @returns {Promise<void>}
   */
  async runMigrations(fromVersion) {
    console.log(`Running migrations: v${fromVersion} → v${this.CURRENT_VERSION}`)

    // Run each migration sequentially
    for (let v = fromVersion + 1; v <= this.CURRENT_VERSION; v++) {
      await this.runSingleMigration(v)
    }

    console.log('All migrations completed successfully')
  }

  /**
   * Run a single migration
   * @private
   * @param {number} version - Target version to migrate to
   * @returns {Promise<void>}
   */
  async runSingleMigration(version) {
    const migration = this.migrations[version]

    if (!migration) {
      throw new Error(
        `Migration v${version} not defined. ` +
        'Please add migration definition to migrations array.'
      )
    }

    console.log(`[Migration v${version}] ${migration.description}`)

    try {
      // Begin transaction
      await this.db.execute('BEGIN TRANSACTION')

      // Run migration
      await migration.up(this.db)

      // Update version
      await this.setVersion(version)

      // Commit transaction
      await this.db.execute('COMMIT')

      console.log(`[Migration v${version}] Complete`)
    } catch (error) {
      // Rollback on failure
      console.error(`[Migration v${version}] Failed:`, error)
      await this.db.execute('ROLLBACK')
      throw new Error(
        `Migration to v${version} failed: ${error.message}. ` +
        'Database rolled back to previous state.'
      )
    }
  }

  /**
   * Get current version number for external use
   * @returns {number}
   */
  getExpectedVersion() {
    return this.CURRENT_VERSION
  }
}
